import { Stepper } from '@seek/hirer-team-member-job-access';
import { useTranslations } from '@vocab/react';
import { Box } from 'braid-design-system';
import React, { useState } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';

import {
  FEATURE_DATA_ANALYTICS,
  useFeatureToggleData,
} from 'src/app/featureToggle';
import {
  dispatchCompleteSetting,
  dispatchResetTeamMemberNotifyState,
  dispatchSettingWithServerError,
  dispatchUpdateActiveNotificationStep,
} from 'src/app/state/dispatcher/notificationsSetting';
import type AppState from 'src/types/appState';

import translations from './.vocab';
import { StepNotificationPreference } from './components/StepNotificationPreference';
import { StepTeamAccess } from './components/StepTeamAccess';
import { StepTeamEmail } from './components/StepTeamEmail';
import { sendOnProgressPointClick } from './utils/sendNotificationsSettingEvents';

import * as styles from './index.css';

export const NotificationsSettingSteps = () => {
  const {
    activeNotificationStep,
    hasTeamMember,
    hasTeamMemberNotifySelected,
    tealium,
  } = useSelector((state: AppState) => {
    const notificationsSetting = state.session.notificationsSetting;
    return {
      activeNotificationStep: notificationsSetting.activeNotificationStep ?? 0,
      advertiserId: state.session.advertiserId,
      hasTeamMember:
        (notificationsSetting.savedTeamMemberJobAccessList ?? []).length > 0,
      hasTeamMemberNotifySelected:
        notificationsSetting.hasTeamMemberNotifySelected ?? false,
      tealium: state.tealium,
    };
  }, shallowEqual);
  const { t } = useTranslations(translations);
  const dispatch = useDispatch();
  const [initialStep, setInitialStep] = useState({
    initialised: false,
    value: 0,
  });

  const onCompleteSetting = () => dispatch(dispatchCompleteSetting());

  const handleInitialActiveStepIndex = () => {
    if (initialStep.initialised) {
      return initialStep.value;
    }

    const isInitiallyEmailNotifyStep = activeNotificationStep === 2;
    const teamAccessStepIndex = 1;
    const initialStepIndexValue = isInitiallyEmailNotifyStep
      ? teamAccessStepIndex
      : activeNotificationStep;

    setInitialStep({
      initialised: true,
      value: initialStepIndexValue,
    });
    dispatch(dispatchResetTeamMemberNotifyState(initialStepIndexValue));

    return initialStepIndexValue;
  };
  const isPageAnalyticsEnabled = useFeatureToggleData(FEATURE_DATA_ANALYTICS);

  const onServerError = () => dispatch(dispatchSettingWithServerError());

  const onClickHeaderPoint = (fromStepIndex: number, toStepIndex: number) =>
    sendOnProgressPointClick(
      tealium,
      fromStepIndex,
      toStepIndex,
      isPageAnalyticsEnabled,
    );

  const stepNames = [t('notification-setting-steps-notifications-step')];
  if (hasTeamMember) {
    stepNames.push(t('notification-setting-steps-select-team'));
  }
  if (hasTeamMemberNotifySelected) {
    stepNames.push(t('notification-setting-steps-email-team'));
  }
  const onActiveStepChanged = (newActiveStep: number) => {
    dispatch(dispatchUpdateActiveNotificationStep(newActiveStep));
  };
  const stepperTitle = t('notification-setting-steps-title');

  return (
    <Box id="notifications-stepper">
      <Stepper
        onComplete={onCompleteSetting}
        stepContentClassName={styles.notificationStepContent}
        stepHeaderClassName={
          hasTeamMember
            ? styles.notificationStepHeader
            : styles.notificationStepNoTeamMemberHeader
        }
        stepBodyClassName={styles.notificationStepBody}
        onServerError={onServerError}
        stepNames={stepNames}
        initialActiveStepIndex={handleInitialActiveStepIndex()}
        onActiveStepChanged={onActiveStepChanged}
        title={stepperTitle}
        onClickHeaderPoint={onClickHeaderPoint}
      >
        <StepNotificationPreference />
        <StepTeamAccess hasNextStep={hasTeamMemberNotifySelected} />
        <StepTeamEmail />
      </Stepper>
    </Box>
  );
};
